import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import useLayout from "../hooks/useLayout.js";
import { GENERIC_IMAGE_DROPZONE_ACCEPT } from "../utils/imageFiles.js";

const baseStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#ced4da",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  transition: "border .3s ease-in-out",
};

const activeStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

const STANDARD_MAX_FILE_BYTES = 10 * 1024 * 1024;
const LARGE_MAX_FILE_BYTES = 50 * 1024 * 1024;

const dropzoneMaxFileBytes = (uploadType) => (
  uploadType === "image" || uploadType === "audio"
    ? STANDARD_MAX_FILE_BYTES
    : LARGE_MAX_FILE_BYTES
);

const fileMaxBytes = (uploadType, file) => (
  uploadType === "video"
  || uploadType === "documents"
  || String(file?.type || "").startsWith("video/")
    ? LARGE_MAX_FILE_BYTES
    : STANDARD_MAX_FILE_BYTES
);

const fileSizeLabel = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;
const dropzoneErrorMessage = (error, maxFileBytes) => (
  error?.code === "file-too-large"
    ? `Arquivo muito grande. Tamanho máximo: ${fileSizeLabel(maxFileBytes)}`
    : error?.message
);

function DropzoneComponent({
  className,
  type,
  handlePreviewSuccess,
  handlePreviewError,
  setFieldValue,
  handleSelectChange,
  value,
  folder,
  id,
  field,
  setFieldID
}) {
  const { setLoading } = useLayout();
  const [files, setFiles] = useState([]);
  const uploadType = type;
  const maxFileBytes = dropzoneMaxFileBytes(uploadType);
  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      const rejectionErrors = fileRejections.map(rejection =>
        rejection.errors.map(error => dropzoneErrorMessage(error, maxFileBytes)).join(', ')
      ).join('; ');
      handlePreviewError(`Arquivo rejeitado: ${rejectionErrors}`);
      setFiles([]); // Clear rejected files
    } else {
      setFiles(acceptedFiles);
    }
  }, [handlePreviewError, maxFileBytes]);

  switch (type) {
    case "image":
      type = GENERIC_IMAGE_DROPZONE_ACCEPT;
      break;
    case "audio":
      type = {
        "audio/mp3": [".mp3", ".wav"],
      };
      break;
    case "video":
      type = {
        "video/mp4": [".mp4", ".webm"],
      };
      break;
    case "documents":
      type = {
        "application/pdf": [".pdf"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
        "text/plain": [".txt"],
        "text/csv": [".csv"],
        "application/zip": [".zip"],
        "application/x-rar-compressed": [".rar"],
        "application/x-tar": [".tar"],
        "application/gzip": [".gz"]
      };
      break;
    default:
      type = "image/*,audio/*,video/*";
      break;
  }

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: type,
    maxFiles: 1, // Only accept one file
    maxSize: maxFileBytes,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  useEffect(() => {
    // Only process the first file if multiple files are somehow selected
    const file = files[0];
    if (file) {
      if (file.errors) {
        handlePreviewError("Arquivo inválido");
      } else {
        const sizeLimit = fileMaxBytes(uploadType, file);
        const sizeLimitMB = fileSizeLabel(sizeLimit);

        if (file.size > sizeLimit) {
          handlePreviewError(`Arquivo muito grande. Tamanho máximo: ${sizeLimitMB}`);
        } else {
          setLoading(true)
          let requestUrl = `/api/upload/${folder}/${id}`;
          const formData = new FormData();
          formData.append("file", file);
          axios.post(requestUrl, formData, {
            headers: { "x-api-key": process.env.REACT_APP_SECRET_API_KEY },
          })
            .then((response) => {
              if (response.data.success === true) {
                if (handleSelectChange) {
                  handleSelectChange(value, field, response.data.message);
                } else {
                  if (setFieldID) {
                    handlePreviewSuccess(
                      response.data.id,
                      setFieldValue,
                      setFieldID,
                      response.data
                    );
                  }
                  handlePreviewSuccess(
                    response.data.message,
                    setFieldValue,
                    field,
                    response.data
                  );
                }
              } else {
                handlePreviewError("Error");
              }
              setLoading(false)
            })
            .catch((error) => {
              setLoading(false)
              handlePreviewError(error);
            });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <section
      className={`w-80 m-auto cursor-pointer ${className}`}
      data-error-path={field || undefined}
    >
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <span>Adicionar arquivo</span>
      </div>
    </section>
  );
}

// Multiple files dropzone component for gallery
export function MultipleDropzoneComponent({
  className,
  type,
  handlePreviewSuccessMultiple,
  handlePreviewError,
  folder,
  id,
  dataTable
}) {
  const { setLoading } = useLayout();
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const uploadType = type;
  const maxFileBytes = dropzoneMaxFileBytes(uploadType);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      const rejectionErrors = fileRejections.map(rejection =>
        `${rejection.file.name}: ${rejection.errors.map(error => dropzoneErrorMessage(error, maxFileBytes)).join(', ')}`
      ).join('; ');
      handlePreviewError(`Arquivos rejeitados: ${rejectionErrors}`);
    }
    // Filter out rejected files silently to avoid breaking the upload cycle
    setFiles(acceptedFiles);
  }, [handlePreviewError, maxFileBytes]);

  // Handle file type definitions same as original component
  switch (type) {
    case "image":
      type = GENERIC_IMAGE_DROPZONE_ACCEPT;
      break;
    case "audio":
      type = {
        "audio/mp3": [".mp3", ".wav"],
      };
      break;
    case "video":
      type = {
        "video/mp4": [".mp4", ".webm"],
      };
      break;
    case "documents":
      type = {
        "application/pdf": [".pdf"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
        "text/plain": [".txt"],
        "text/csv": [".csv"],
        "application/zip": [".zip"],
        "application/x-rar-compressed": [".rar"],
        "application/x-tar": [".tar"],
        "application/gzip": [".gz"]
      };
      break;
    default:
      type = "image/*,audio/*,video/*";
      break;
  }

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: type,
    multiple: true, // Enable multiple file selection
    maxSize: maxFileBytes,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  useEffect(() => {
    if (files.length === 0) return;

    setIsUploading(true);
    setLoading(true);

    const uploadPromises = files
      .filter(file => {
        const sizeLimit = fileMaxBytes(uploadType, file);

        // Silently filter out invalid files
        if (file.size > sizeLimit) {
          return false;
        }
        return true;
      })
      .map(file => {
        let requestUrl = `/api/upload/${folder}/${id}`;
        const formData = new FormData();
        formData.append("file", file);
        return axios.post(requestUrl, formData, {
          headers: { "x-api-key": process.env.REACT_APP_SECRET_API_KEY },
        })
          .then((response) => {
            if (response.data.success === true) {
              return {
                success: true,
                filename: response.data.message,
                id: response.data.id || null,
                width: response.data.width || response.data.dimensions?.width || null,
                height: response.data.height || response.data.dimensions?.height || null
              };
            } else {
              return { success: false, error: "Upload failed" };
            }
          })
          .catch((error) => {
            return { success: false, error: error.message };
          });
      });

    Promise.all(uploadPromises)
      .then((results) => {
        const successfulUploads = results.filter(result => result.success);

        if (successfulUploads.length > 0) {
          handlePreviewSuccessMultiple(successfulUploads);
        }

        // Show error only if all uploads failed
        const failedUploads = results.filter(result => !result.success);
        if (failedUploads.length === results.length && results.length > 0) {
          handlePreviewError("Falha ao fazer upload dos arquivos");
        }

        setIsUploading(false);
        setLoading(false);
        setFiles([]); // Reset files after upload
      })
      .catch((error) => {
        setIsUploading(false);
        setLoading(false);
        setFiles([]);
        handlePreviewError("Erro durante o upload dos arquivos");
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <section className={`w-80 m-auto cursor-pointer ${className}`}>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <span>
          {isUploading ? "Enviando arquivos..." : "Adicionar arquivos (múltiplos)"}
        </span>
      </div>
    </section>
  );
}

export default DropzoneComponent;
